import { useState, useEffect, useCallback } from 'react';
import { defaultConfig } from '../data/config';

const STORAGE_KEY = 'creator-website-config-v1';

export function useEditableConfig() {
  const [config, setConfig] = useState(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        try {
          const parsed = JSON.parse(saved);
          return {
            ...defaultConfig,
            ...parsed,
            profile: { ...defaultConfig.profile, ...(parsed.profile || {}) },
            platforms: { ...defaultConfig.platforms, ...(parsed.platforms || {}) },
            stats: { ...defaultConfig.stats, ...(parsed.stats || {}) },
            works: parsed.works || defaultConfig.works,
            collaboration: { ...defaultConfig.collaboration, ...(parsed.collaboration || {}) },
            social: { ...defaultConfig.social, ...(parsed.social || {}) },
            contact: { ...defaultConfig.contact, ...(parsed.contact || {}) },
          };
        } catch (e) {
          console.error('Failed to parse saved config:', e);
        }
      }
    }
    return defaultConfig;
  });

  const [isEditing, setIsEditing] = useState(false);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(config));
    } catch (err) {
      console.error('Failed to save config:', err);
    }
  }, [config]);

  const updateProfile = useCallback((updates) => {
    setConfig(prev => ({
      ...prev,
      profile: { ...prev.profile, ...updates }
    }));
  }, []);

  const updateStats = useCallback((updates) => {
    setConfig(prev => ({
      ...prev,
      stats: { ...prev.stats, ...updates }
    }));
  }, []);

  const updatePlatforms = useCallback((updates) => {
    setConfig(prev => ({
      ...prev,
      platforms: { ...prev.platforms, ...updates }
    }));
  }, []);

  const updatePlatform = useCallback((platformKey, updates) => {
    setConfig(prev => ({
      ...prev,
      platforms: {
        ...prev.platforms,
        [platformKey]: { ...prev.platforms[platformKey], ...updates }
      }
    }));
  }, []);

  const updateWorks = useCallback((works) => {
    setConfig(prev => ({ ...prev, works }));
  }, []);

  const addWork = useCallback((work) => {
    setConfig(prev => ({
      ...prev,
      works: [...prev.works, { ...work, id: Date.now() }]
    }));
  }, []);

  const deleteWork = useCallback((id) => {
    setConfig(prev => ({
      ...prev,
      works: prev.works.filter(w => w.id !== id)
    }));
  }, []);

  const updateCollaboration = useCallback((updates) => {
    setConfig(prev => ({
      ...prev,
      collaboration: { ...prev.collaboration, ...updates }
    }));
  }, []);

  const updateSocial = useCallback((updates) => {
    setConfig(prev => ({
      ...prev,
      social: { ...prev.social, ...updates }
    }));
  }, []);

  const updateContact = useCallback((updates) => {
    setConfig(prev => ({
      ...prev,
      contact: { ...prev.contact, ...updates }
    }));
  }, []);

  const resetToDefault = useCallback(() => {
    if (confirm('确定要重置为默认配置吗？所有自定义内容将丢失。')) {
      setConfig(defaultConfig);
      localStorage.removeItem(STORAGE_KEY);
    }
  }, []);

  const exportConfig = useCallback(() => {
    const dataStr = JSON.stringify(config, null, 2);
    const blob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `creator-website-config-${new Date().toISOString().split('T')[0]}.json`;
    link.click();
    URL.revokeObjectURL(url);
  }, [config]);

  const importConfig = useCallback((file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const imported = JSON.parse(e.target.result);
          setConfig({
            ...defaultConfig,
            ...imported,
            profile: { ...defaultConfig.profile, ...(imported.profile || {}) },
            platforms: { ...defaultConfig.platforms, ...(imported.platforms || {}) },
            stats: { ...defaultConfig.stats, ...(imported.stats || {}) },
            works: imported.works || defaultConfig.works,
            collaboration: { ...defaultConfig.collaboration, ...(imported.collaboration || {}) },
            social: { ...defaultConfig.social, ...(imported.social || {}) },
            contact: { ...defaultConfig.contact, ...(imported.contact || {}) },
          });
          resolve();
        } catch (err) {
          reject(err);
        }
      };
      reader.onerror = reject;
      reader.readAsText(file);
    });
  }, []);

  return {
    config,
    isEditing,
    setIsEditing,
    updateProfile,
    updateStats,
    updatePlatforms,
    updatePlatform,
    updateWorks,
    addWork,
    deleteWork,
    updateCollaboration,
    updateSocial,
    updateContact,
    resetToDefault,
    exportConfig,
    importConfig
  };
}
